import * as React from "react";
import { cn } from "@/lib/utils";
import { inputBaseClassName } from "./input";

const formatter = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatBs(value: number): string {
  return formatter.format(value);
}

export interface ParseOptions {
  allowNegative?: boolean;
}

export function parseBs(text: string, opts: ParseOptions = {}): number | null {
  const { allowNegative = false } = opts;
  const trimmed = text.trim();
  if (trimmed === "") return null;

  // Detect leading minus before stripping non-numerics.
  const isNegative = allowNegative && trimmed.startsWith("-");

  // Keep digits, dots, commas only.
  const cleaned = trimmed.replace(/[^\d.,]/g, "");
  if (cleaned === "") return null;

  // Decide which mark is the decimal separator.
  const lastComma = cleaned.lastIndexOf(",");
  const lastDot = cleaned.lastIndexOf(".");

  let decimalIndex = -1;
  if (lastComma !== -1 && lastDot !== -1) {
    // Both present: the last one is the decimal.
    decimalIndex = Math.max(lastComma, lastDot);
  } else if (lastComma !== -1 || lastDot !== -1) {
    // Only one mark present.
    const idx = Math.max(lastComma, lastDot);
    const digitsAfter = cleaned.length - idx - 1;
    // 1-2 digits after = decimal, 0 or 3+ = thousand separator.
    if (digitsAfter >= 1 && digitsAfter <= 2) decimalIndex = idx;
  }

  let intPart: string;
  let fracPart: string;
  if (decimalIndex === -1) {
    intPart = cleaned.replace(/[.,]/g, "");
    fracPart = "";
  } else {
    intPart = cleaned.slice(0, decimalIndex).replace(/[.,]/g, "");
    fracPart = cleaned.slice(decimalIndex + 1).replace(/[.,]/g, "");
  }

  if (intPart === "" && fracPart === "") return null;

  const numericString = `${intPart || "0"}.${fracPart || "0"}`;
  const n = Number(numericString);
  if (!Number.isFinite(n)) return null;

  // Round to 2 decimals to avoid 0.1+0.2 drift.
  const rounded = Math.round(n * 100) / 100;
  return isNegative ? -rounded : rounded;
}

export interface CurrencyInputProps {
  value: number | null;
  onChange: (value: number | null) => void;
  suffix?: string;
  allowNegative?: boolean;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  className?: string;
  id?: string;
  "aria-invalid"?: boolean;
}

// Count how many digits exist in `s.slice(0, end)`.
function countDigitsBefore(s: string, end: number): number {
  let n = 0;
  for (let i = 0; i < end && i < s.length; i++) {
    if (s.charCodeAt(i) >= 48 && s.charCodeAt(i) <= 57) n++;
  }
  return n;
}

// Find index in `s` such that there are `target` digits to its left.
function indexAfterDigits(s: string, target: number): number {
  if (target <= 0) return 0;
  let n = 0;
  for (let i = 0; i < s.length; i++) {
    if (s.charCodeAt(i) >= 48 && s.charCodeAt(i) <= 57) {
      n++;
      if (n === target) return i + 1;
    }
  }
  return s.length;
}

// Format a parsed numeric value as the user would see it mid-typing.
// Differs from formatBs because we preserve "in-progress" decimals.
export function formatLive(
  raw: string,
  allowNegative: boolean,
): { display: string; numeric: number | null } {
  const trimmed = raw.replace(/\s/g, "");
  if (trimmed === "" || trimmed === "-") {
    return { display: trimmed === "-" && allowNegative ? "-" : "", numeric: null };
  }

  const isNegative = allowNegative && trimmed.startsWith("-");
  const body = isNegative ? trimmed.slice(1) : trimmed.replace(/^-/, "");

  // Determine which mark is the decimal (same logic as parseBs).
  const cleaned = body.replace(/[^\d.,]/g, "");
  if (cleaned === "") return { display: isNegative ? "-" : "", numeric: null };

  const lastComma = cleaned.lastIndexOf(",");
  const lastDot = cleaned.lastIndexOf(".");
  let decimalIndex = -1;
  if (lastComma !== -1 && lastDot !== -1) {
    decimalIndex = Math.max(lastComma, lastDot);
  } else if (lastComma !== -1 || lastDot !== -1) {
    const idx = Math.max(lastComma, lastDot);
    const digitsAfter = cleaned.length - idx - 1;
    // 0 digits after = trailing separator the user just typed (in-progress
    // decimal); 1-2 = decimal. 3+ = thousand separator. Grouping marks are
    // auto-inserted, so a user-typed trailing mark is always a decimal point.
    if (digitsAfter >= 0 && digitsAfter <= 2) decimalIndex = idx;
  }

  let intDigits: string;
  let fracDigits: string;
  let trailingMark = false;
  if (decimalIndex === -1) {
    intDigits = cleaned.replace(/[.,]/g, "");
    fracDigits = "";
  } else {
    intDigits = cleaned.slice(0, decimalIndex).replace(/[.,]/g, "");
    fracDigits = cleaned.slice(decimalIndex + 1).replace(/[.,]/g, "").slice(0, 2);
    trailingMark = decimalIndex === cleaned.length - 1;
  }

  // Strip leading zeros on int part (but keep a single 0 if all zeros).
  const intStripped = intDigits.replace(/^0+(?=\d)/, "") || (intDigits ? "0" : "");
  const intGrouped = intStripped.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

  let display = intGrouped;
  if (trailingMark) display += ",";
  else if (fracDigits) display += "," + fracDigits;
  if (isNegative) display = "-" + display;

  // Compute numeric value.
  let numeric: number | null = null;
  if (intStripped !== "" || fracDigits !== "") {
    const n = Number(`${intStripped || "0"}.${fracDigits || "0"}`);
    if (Number.isFinite(n)) {
      numeric = Math.round(n * 100) / 100;
      if (isNegative) numeric = -numeric;
    }
  }

  return { display, numeric };
}

export function CurrencyInput({
  value,
  onChange,
  suffix = "KM",
  allowNegative = false,
  placeholder = "0,00",
  disabled,
  autoFocus,
  className,
  id,
  "aria-invalid": ariaInvalid,
}: CurrencyInputProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [display, setDisplay] = React.useState<string>(
    value === null ? "" : formatBs(value),
  );
  const isFocusedRef = React.useRef(false);
  const pendingCaretRef = React.useRef<number | null>(null);

  // Sync external value when not focused (avoid stomping user input mid-typing).
  React.useEffect(() => {
    if (isFocusedRef.current) return;
    setDisplay(value === null ? "" : formatBs(value));
  }, [value]);

  // Apply pending caret position after render.
  React.useLayoutEffect(() => {
    const el = inputRef.current;
    const pos = pendingCaretRef.current;
    if (el && pos !== null) {
      el.setSelectionRange(pos, pos);
      pendingCaretRef.current = null;
    }
  });

  // Process a raw input string + caret position into the final display state.
  // Used by both onChange and the Backspace-through-separator handler.
  const processInput = (rawValue: string, rawCaret: number) => {
    let stripped = "";
    let strippedCaret = 0;
    for (let i = 0; i < rawValue.length; i++) {
      const ch = rawValue[i]!;
      const ok = allowNegative ? /[\d.,\-]/.test(ch) : /[\d.,]/.test(ch);
      if (ok) {
        // Only allow `-` at position 0.
        if (ch === "-" && stripped.length > 0) continue;
        stripped += ch;
        if (i < rawCaret) strippedCaret++;
      }
    }

    const digitsBefore = countDigitsBefore(stripped, strippedCaret);
    const { display: newDisplay, numeric } = formatLive(stripped, allowNegative);

    let newCaret = indexAfterDigits(newDisplay, digitsBefore);
    if (newDisplay.endsWith(",") && digitsBefore === countDigitsBefore(newDisplay, newDisplay.length)) {
      newCaret = newDisplay.length;
    }
    if (newDisplay.startsWith("-") && newCaret === 0) newCaret = 1;

    setDisplay(newDisplay);
    pendingCaretRef.current = newCaret;
    onChange(numeric);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const el = e.target;
    processInput(el.value, el.selectionStart ?? el.value.length);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    const el = e.currentTarget;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    if (start !== end) return;

    // Backspace at a separator: delete the digit before AND the separator,
    // so separators behave like "skin" over digits.
    if (e.key === "Backspace" && start >= 2) {
      const ch = display[start - 1];
      if (ch === "." || ch === ",") {
        e.preventDefault();
        const newRaw = display.slice(0, start - 2) + display.slice(start);
        processInput(newRaw, start - 2);
      }
      return;
    }

    // Delete (forward-delete) at a separator: mirror — drop the separator AND the digit after it.
    if (e.key === "Delete" && start <= display.length - 2) {
      const ch = display[start];
      if (ch === "." || ch === ",") {
        e.preventDefault();
        const newRaw = display.slice(0, start) + display.slice(start + 2);
        processInput(newRaw, start);
      }
      return;
    }
  };

  const handleBlur = () => {
    isFocusedRef.current = false;
    if (value === null) {
      setDisplay("");
    } else {
      setDisplay(formatBs(value));
    }
  };

  const handleFocus = () => {
    isFocusedRef.current = true;
  };

  return (
    <div className={cn("relative", className)}>
      <input
        ref={inputRef}
        id={id}
        type="text"
        inputMode="decimal"
        data-slot="input"
        value={display}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        aria-invalid={ariaInvalid}
        className={cn(inputBaseClassName, suffix && "pr-12")}
      />
      {suffix && (
        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-muted-foreground">
          {suffix}
        </span>
      )}
    </div>
  );
}
